import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHero } from "../components/PageShell";
import { Figure } from "../components/Figure";
import { imageUrl } from "../lib/imageUrl";
import { GALLERY_ALBUMS, getGalleryAlbum } from "../lib/gallery";
import { ROUTES } from "../lib/routes";

/**
 * One album: a natural-ratio masonry of its photos, each opening a lightbox.
 * The lightbox is deliberately minimal — the photograph, its caption, and
 * prev/next — driven by arrow keys and Escape as well as the buttons.
 */
export default function GalleryAlbum() {
  const { album: id } = useParams();
  const album = getGalleryAlbum(id);
  const [open, setOpen] = useState<number | null>(null);

  const step = useCallback(
    (delta: number) => {
      if (album == null) return;
      setOpen((i) => (i == null ? i : (i + delta + album.photos.length) % album.photos.length));
    },
    [album],
  );

  useEffect(() => {
    if (open == null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(null);
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open != null, step]);

  if (!album) {
    return (
      <section className="section">
        <div className="container">
          <h1>Album not found</h1>
          <p>
            <Link to={ROUTES.gallery}>Back to the gallery</Link>
          </p>
        </div>
      </section>
    );
  }

  const others = GALLERY_ALBUMS.filter((a) => a.id !== album.id).slice(0, 4);
  const current = open != null ? album.photos[open] : null;

  return (
    <>
      <PageHero
        variant="hero-editorial"
        eyebrow="Student Life · Gallery"
        title={album.title}
        lead={`${album.photos.length} photograph${album.photos.length > 1 ? "s" : ""} from the institute's own record.`}
        crumbs={[{ label: "Gallery", to: ROUTES.gallery }, { label: album.title }]}
        image={null}
      />

      <section className="section">
        <div className="container">
          <div className="gal-masonry">
            {album.photos.map((p, i) => (
              <button
                key={p.url}
                type="button"
                className="gal-shot reveal"
                onClick={() => setOpen(i)}
                aria-label={`Open photo: ${p.alt}`}
              >
                <Figure
                  media={p}
                  fallbackLabel={album.title.toUpperCase()}
                  aspect={`${p.width}/${p.height}`}
                  sizes="(max-width: 640px) 100vw, (max-width: 1000px) 48vw, 30vw"
                />
              </button>
            ))}
          </div>

          <div className="gal-siblings">
            <span className="kicker">More albums</span>
            {others.map((a) => (
              <Link key={a.id} to={`${ROUTES.gallery}/${a.id}`}>
                {a.title} ({a.photos.length})
              </Link>
            ))}
            <Link to={ROUTES.gallery}>All albums →</Link>
          </div>
        </div>
      </section>

      {current && open != null && (
        <div className="gal-lightbox" role="dialog" aria-modal="true" aria-label={current.alt} onClick={() => setOpen(null)}>
          <figure onClick={(e) => e.stopPropagation()}>
            <img src={imageUrl(current.url, { width: 1600 })} alt={current.alt} />
            <figcaption>
              {current.alt}
              <span className="gal-lb-count">
                {open + 1} / {album.photos.length}
              </span>
            </figcaption>
          </figure>
          <button type="button" className="gal-lb-btn gal-lb-close" onClick={() => setOpen(null)} aria-label="Close">
            ×
          </button>
          <button
            type="button"
            className="gal-lb-btn gal-lb-prev"
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
            aria-label="Previous photo"
          >
            ←
          </button>
          <button
            type="button"
            className="gal-lb-btn gal-lb-next"
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
            aria-label="Next photo"
          >
            →
          </button>
        </div>
      )}
    </>
  );
}
